import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Siren, BookOpen, HeartHandshake, Phone, CheckCircle2 } from "lucide-react";

const disasterTypes = ["Banjir", "Tanah Longsor", "Kebakaran Hutan & Lahan", "Angin Puting Beliung", "Abrasi Pantai", "Gempa Bumi", "Kekeringan", "Lainnya"];

const DL = {
  caraLapor: "Pastikan Anda berada di tempat aman\nIsi formulir dengan data yang benar dan lokasi yang jelas\nSertakan nomor telepon aktif agar petugas dapat menghubungi\nUntuk kondisi darurat yang mengancam jiwa, telepon 112",
  sebelum: "Kenali risiko bencana di lingkungan Anda\nSiapkan tas siaga bencana (dokumen, obat, senter)\nKetahui jalur evakuasi dan titik kumpul terdekat\nIkuti pelatihan dan simulasi bencana",
  saat: "Tetap tenang dan jangan panik\nIkuti arahan petugas dan aparat setempat\nEvakuasi melalui jalur yang telah ditentukan\nHindari daerah berbahaya (sungai, lereng, jaringan listrik)",
  sesudah: "Pastikan kondisi aman sebelum kembali ke rumah\nPeriksa anggota keluarga dan tetangga\nLaporkan kerusakan kepada BPBD/aparat desa\nWaspadai bencana susulan",
  mitra: "TNI, POLRI, Dinas Sosial, Dinas Kesehatan, PMI, Basarnas, organisasi relawan, dan dunia usaha di wilayah Kayong Utara.",
  partisipasi: "Bergabung sebagai relawan penanggulangan bencana melalui pendaftaran di kantor BPBD atau komunitas Desa Tangguh Bencana.",
  bantuan: "Bantuan logistik dan dana untuk korban bencana disalurkan melalui posko resmi BPBD dengan pencatatan transparan.",
};

const toLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export default function Layanan() {
  const { c } = useContent();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", phone: "", disaster_type: "", location: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (location.hash === "#lapor") {
      setTimeout(() => document.getElementById("lapor")?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [location]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.disaster_type || !form.location || !form.description) {
      toast.error("Mohon lengkapi semua kolom formulir.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reports", form);
      setSuccess(true);
      setForm({ name: "", phone: "", disaster_type: "", location: "", description: "" });
      toast.success("Laporan berhasil dikirim. Tim BPBD akan segera menindaklanjuti.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="layanan-page">
      <PageHeader title="Layanan Publik" subtitle="Lapor bencana, panduan edukasi kebencanaan, dan informasi kerjasama kemitraan" />

      <section id="lapor" className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Siren className="text-red-600" size={26} />
            <h2 className="font-heading text-lg font-bold text-slate-900">Lapor Bencana</h2>
          </div>
          <p className="text-slate-600 text-sm md:text-base mb-6">
            Laporkan kejadian bencana di wilayah Anda. Laporan akan diterima langsung oleh petugas BPBD
            dan ditindaklanjuti sesuai prosedur tanggap darurat.
          </p>
          <div className="bg-slate-950 text-white rounded p-6 mb-6">
            <p className="font-heading font-semibold mb-3 flex items-center gap-2"><Phone size={18} className="text-orange-500" /> Darurat? Telepon Langsung!</p>
            <p className="font-mono text-3xl font-bold text-orange-500">{c("kontak_darurat", "112")}</p>
            <p className="text-sm text-slate-400 mt-1">Call Center 24 jam · atau Posko BPBD {c("kontak_telepon", "(0534) 771-112")}</p>
          </div>
          <div className="border border-slate-200 rounded-xl p-6 bg-white">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">Cara Melaporkan Bencana</h3>
            <ol className="text-sm text-slate-600 space-y-2 list-decimal pl-4" data-testid="cara-lapor-list">
              {toLines(c("layanan_cara_lapor", DL.caraLapor)).map((x) => <li key={x}>{x}</li>)}
            </ol>
          </div>
        </div>
        <div className="border border-slate-200 bg-white rounded p-7">
          {success ? (
            <div className="text-center py-16" data-testid="report-success">
              <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={48} />
              <h3 className="font-heading text-xl font-bold text-slate-900">Laporan Terkirim</h3>
              <p className="text-slate-600 text-sm mt-2">Terima kasih. Tim BPBD akan segera memverifikasi dan menindaklanjuti laporan Anda.</p>
              <Button onClick={() => setSuccess(false)} variant="outline" className="mt-6" data-testid="report-again-btn">Buat Laporan Lain</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="lapor-form">
              <h3 className="font-heading text-xl font-bold text-slate-900">Formulir Laporan Kejadian</h3>
              <div className="space-y-2">
                <Label htmlFor="name">Nama Pelapor</Label>
                <Input id="name" data-testid="lapor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Nomor Telepon / WA</Label>
                <Input id="phone" data-testid="lapor-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" />
              </div>
              <div className="space-y-2">
                <Label>Jenis Bencana</Label>
                <Select value={form.disaster_type} onValueChange={(v) => setForm({ ...form, disaster_type: v })}>
                  <SelectTrigger data-testid="lapor-type-select"><SelectValue placeholder="Pilih jenis bencana" /></SelectTrigger>
                  <SelectContent>
                    {disasterTypes.map((t) => <SelectItem key={t} value={t} data-testid={`lapor-type-${t.toLowerCase().replace(/\s|&/g, "-")}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Lokasi Kejadian</Label>
                <Input id="location" data-testid="lapor-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Desa/Dusun, Kecamatan" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Uraian Kejadian</Label>
                <Textarea id="description" data-testid="lapor-description-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Jelaskan kondisi kejadian, jumlah warga terdampak, kebutuhan mendesak, dll." />
              </div>
              <Button type="submit" disabled={submitting} className="btn-pop w-full bg-orange-600 hover:bg-orange-500 rounded-full font-bold" data-testid="lapor-submit-btn">
                {submitting ? "Mengirim..." : "Kirim Laporan"}
              </Button>
            </form>
          )}
        </div>
      </section>

      <section className="bg-slate-100 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center gap-3 mb-8">
            <BookOpen className="text-orange-600" size={24} />
            <h2 className="font-heading text-lg font-bold text-slate-900">Panduan & Edukasi</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Sebelum Bencana", tips: toLines(c("layanan_sebelum", DL.sebelum)) },
              { title: "Saat Bencana", tips: toLines(c("layanan_saat", DL.saat)) },
              { title: "Sesudah Bencana", tips: toLines(c("layanan_sesudah", DL.sesudah)) },
            ].map((p) => (
              <div key={p.title} className="hover-lift bg-white border border-slate-200 rounded-xl p-6" data-testid={`edukasi-${p.title.toLowerCase().replace(/\s/g, "-")}`}>
                <h3 className="font-heading font-bold text-orange-600 mb-4">{p.title}</h3>
                <ul className="space-y-2.5 text-sm text-slate-600">
                  {p.tips.map((t) => <li key={t} className="flex gap-2"><span className="text-orange-500 mt-0.5">✓</span>{t}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-6">
            Buku saku tanggap bencana, video, dan infografis dapat diunduh melalui situs <a href="https://bnpb.go.id" target="_blank" rel="noreferrer" className="text-orange-600 underline">BNPB</a>.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center gap-3 mb-8">
          <HeartHandshake className="text-orange-600" size={24} />
          <h2 className="font-heading text-lg font-bold text-slate-900">Kerjasama & Kemitraan</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="hover-lift border border-slate-200 rounded-xl p-6 bg-white">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">Mitra Kerja</h3>
            <p className="text-sm text-slate-600 whitespace-pre-line">{c("layanan_mitra", DL.mitra)}</p>
          </div>
          <div className="hover-lift border border-slate-200 rounded-xl p-6 bg-white">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">Cara Berpartisipasi</h3>
            <p className="text-sm text-slate-600 whitespace-pre-line">{c("layanan_partisipasi", DL.partisipasi)}</p>
          </div>
          <div className="hover-lift border border-slate-200 rounded-xl p-6 bg-white">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">Penerimaan Bantuan</h3>
            <p className="text-sm text-slate-600 whitespace-pre-line">{c("layanan_bantuan", DL.bantuan)}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
